import type { ArcadeTheme } from "@/utils/arcade/theme";

export interface ShareScoreInput {
  canvas: HTMLCanvasElement | null;
  theme: ArcadeTheme;
  gameName: string;
  songTitle: string;
  score: number;
  level?: number;
  url: string;
}

export type ShareOutcome = "shared" | "copied" | "cancelled" | "failed";

const CARD_WIDTH = 1080;
const CARD_HEIGHT = 1350;

// A portrait score card: the game's last frame behind the score, the song and
// a line inviting a rematch. Sized for stories and chat previews.
async function scoreCard(input: ShareScoreInput): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  try {
    await document.fonts?.ready;
  } catch {
    // Fonts are a nicety; the card still draws with fallbacks.
  }
  const card = document.createElement("canvas");
  card.width = CARD_WIDTH;
  card.height = CARD_HEIGHT;
  const ctx = card.getContext("2d");
  if (!ctx) return null;
  const { palette } = input.theme;
  ctx.fillStyle = palette.backgroundDeep;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  const frame = input.canvas;
  if (frame && frame.width > 0 && frame.height > 0) {
    // Cover the card with the last frame, centred.
    const scale = Math.max(CARD_WIDTH / frame.width, CARD_HEIGHT / frame.height);
    const w = frame.width * scale;
    const h = frame.height * scale;
    try {
      ctx.drawImage(frame, (CARD_WIDTH - w) / 2, (CARD_HEIGHT - h) / 2, w, h);
    } catch {
      // A tainted canvas cannot be read; the plain background stands in.
    }
  }
  const shade = ctx.createLinearGradient(0, 0, 0, CARD_HEIGHT);
  shade.addColorStop(0, "rgba(0,0,0,0.15)");
  shade.addColorStop(0.45, "rgba(0,0,0,0.35)");
  shade.addColorStop(1, "rgba(0,0,0,0.85)");
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  ctx.textAlign = "center";
  ctx.fillStyle = palette.accent;
  ctx.font = "700 34px Poppins, sans-serif";
  ctx.fillText(`${input.gameName.toUpperCase()}  ·  TRUEVINED ARCADE`, CARD_WIDTH / 2, 820);
  ctx.fillStyle = "#ffffff";
  ctx.font = "800 150px Hanson, Poppins, sans-serif";
  ctx.shadowColor = palette.primary;
  ctx.shadowBlur = 40;
  ctx.fillText(input.score.toLocaleString(), CARD_WIDTH / 2, 990);
  ctx.shadowBlur = 0;
  ctx.font = "600 44px Poppins, sans-serif";
  ctx.fillText(input.songTitle, CARD_WIDTH / 2, 1080, CARD_WIDTH - 120);
  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.font = "500 36px Poppins, sans-serif";
  ctx.fillText(input.level && input.level > 1 ? `Reached level ${input.level}. Can you beat it?` : "Can you beat it?", CARD_WIDTH / 2, 1150);
  ctx.fillStyle = palette.primary;
  ctx.font = "600 32px Poppins, sans-serif";
  ctx.fillText(input.url.replace(/^https?:\/\//, ""), CARD_WIDTH / 2, 1260, CARD_WIDTH - 120);

  return new Promise((resolve) => card.toBlob((blob) => resolve(blob), "image/png"));
}

// Shares through the phone's share sheet with the card attached when it can,
// the link alone when files are not supported, and the clipboard otherwise.
export async function shareScore(input: ShareScoreInput): Promise<ShareOutcome> {
  const text = `I scored ${input.score.toLocaleString()} on ${input.gameName} to "${input.songTitle}" by Truevined. Can you beat it?`;
  const nav = typeof navigator !== "undefined" ? navigator : null;
  try {
    if (nav?.share) {
      const blob = await scoreCard(input);
      const file = blob ? new File([blob], `truevined-${input.gameName.toLowerCase().replace(/\s+/g, "-")}-score.png`, { type: "image/png" }) : null;
      if (file && nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title: `${input.gameName} score`, text: `${text} ${input.url}` });
      } else {
        await nav.share({ title: `${input.gameName} score`, text, url: input.url });
      }
      return "shared";
    }
    if (nav?.clipboard?.writeText) {
      await nav.clipboard.writeText(`${text} ${input.url}`);
      return "copied";
    }
    return "failed";
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return "cancelled";
    try {
      await nav?.clipboard?.writeText(`${text} ${input.url}`);
      return "copied";
    } catch {
      return "failed";
    }
  }
}
